/**
 * Chat session sidebar.
 *
 * Lists conversations from /api/v1/chat/conversations, supports create + delete.
 * Highlights the active session and shows a compact "preview" line.
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conversationsApi } from '@/lib/api';
import type { Conversation } from '@/types/chat';
import { Plus, MessageSquare, Trash2 } from 'lucide-react';
import clsx from 'clsx';

interface SessionListProps {
  activeId: string | null;
  onSelect: (id: string) => void;
  onCreated: (id: string) => void;
}

export function SessionList({ activeId, onSelect, onCreated }: SessionListProps) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => conversationsApi.list(),
    refetchInterval: 30_000,
  });

  // Refresh the sidebar immediately when a local-only conversation gets a
  // new turn (AIAccountant flow). Without this, the new chat wouldn't show
  // up until the next 30 s poll or manual refresh.
  useEffect(() => {
    const onUpdate = () => qc.invalidateQueries({ queryKey: ['conversations'] });
    window.addEventListener('axondesktop.conversations.updated', onUpdate);
    return () =>
      window.removeEventListener('axondesktop.conversations.updated', onUpdate);
  }, [qc]);

  const createMut = useMutation({
    mutationFn: () => conversationsApi.create(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      onCreated(res.conversation.id);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => conversationsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  });

  return (
    <aside className="w-72 shrink-0 border-r border-border bg-muted/30 flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Conversations
        </h2>
        <button
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          className="p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-50"
          title="New conversation"
          aria-label="New conversation"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {isLoading && (
          <div className="px-4 py-2 text-sm text-muted-foreground">Loading…</div>
        )}
        {data?.conversations?.length === 0 && !isLoading && (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            No conversations yet.<br />
            <button
              onClick={() => createMut.mutate()}
              className="underline text-primary mt-2"
            >
              Start one
            </button>
          </div>
        )}
        {data?.conversations?.map((c: Conversation) => (
          <SessionRow
            key={c.id}
            session={c}
            isActive={c.id === activeId}
            onSelect={() => onSelect(c.id)}
            onDelete={() => {
              if (confirm(`Delete "${c.title}"?`)) deleteMut.mutate(c.id);
            }}
          />
        ))}
      </div>

      <div className="px-4 py-2 border-t border-border text-[10px] text-muted-foreground">
        Laabha · v0.1
      </div>
    </aside>
  );
}

function SessionRow({
  session,
  isActive,
  onSelect,
  onDelete,
}: {
  session: Conversation;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={clsx(
        'group flex items-start gap-2 px-3 py-2 mx-1 rounded-md cursor-pointer transition-colors',
        isActive ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
      )}
      onClick={onSelect}
    >
      <MessageSquare className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{session.title}</div>
        {session.last_message_preview && (
          <div
            className={clsx(
              'text-xs truncate mt-0.5',
              isActive ? 'text-primary-foreground/70' : 'text-muted-foreground',
            )}
          >
            {session.last_message_preview}
          </div>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className={clsx(
          'opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded',
          isActive ? 'hover:bg-primary-foreground/20' : 'hover:bg-muted-foreground/10',
        )}
        aria-label="Delete"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
